import { type AdminGame, MAX_STORE_HEROES, type StoreHeroes } from '@mcgames/contracts';
import { Button, Select, Spinner } from '@mcgames/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../api/hooks.ts';
import styles from './Admin.module.css';
import { ErrorText } from './games/ErrorText.tsx';
import { GAMES_PATH } from './games/game-hooks.ts';

const HOME_PATH = '/api/store/home';
const HEROES_PATH = '/api/admin/store/heroes';
/** The select value for "no game chosen". */
const NONE = '';

export function StorePage() {
  const { t } = useTranslation('admin');
  const games = useApiQuery<AdminGame[]>(GAMES_PATH);
  const heroes = useApiQuery<StoreHeroes>(HEROES_PATH);
  const failed = games.isError || heroes.isError;

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('store.title')}</h1>
      <p className={styles['muted']}>{t('store.intro')}</p>
      {(games.isPending || heroes.isPending) && !failed && <Spinner />}
      {failed && (
        <p role="alert" className={styles['error']}>
          {t('store.loadFailed')}
        </p>
      )}
      {games.data !== undefined && heroes.data !== undefined && (
        <HeroesForm games={games.data} heroIds={heroes.data.gameIds} />
      )}
    </div>
  );
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

function HeroesForm({ games, heroIds }: { games: AdminGame[]; heroIds: string[] }) {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const byId = new Map(games.map((game) => [game.id, game]));
  // Ids of games the admin list does not know can only be stale; saving them would fail.
  const initialIds = heroIds.filter((id) => byId.has(id));
  const [ids, setIds] = useState(initialIds);
  // The last list known to be stored; the queries are only refetched in the background.
  const [stored, setStored] = useState(initialIds);
  const [candidate, setCandidate] = useState(NONE);
  const save = useApiMutation<StoreHeroes>('PUT', HEROES_PATH, {
    onSuccess: (_, request) => {
      setStored(request.gameIds);
      void queryClient.invalidateQueries({ queryKey: [HEROES_PATH] });
      void queryClient.invalidateQueries({ queryKey: [HOME_PATH] });
    },
  });

  const addable = games.filter((game) => game.published && !ids.includes(game.id));
  const full = ids.length >= MAX_STORE_HEROES;
  const dirty = !sameIds(ids, stored);

  const change = (next: string[]) => {
    save.reset();
    setIds(next);
  };
  const move = (index: number, offset: -1 | 1) => {
    const next = [...ids];
    const [id] = next.splice(index, 1);
    if (id === undefined) return;
    next.splice(index + offset, 0, id);
    change(next);
  };

  return (
    <form
      className={styles['page']}
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate({ gameIds: ids });
      }}
    >
      {ids.length === 0 ? (
        <p className={styles['muted']}>{t('store.empty')}</p>
      ) : (
        <ol className={styles['heroList']} aria-label={t('store.listLabel')}>
          {ids.map((id, index) => {
            const game = byId.get(id);
            if (game === undefined) return null;
            return (
              <HeroItem
                key={id}
                game={game}
                position={index + 1}
                isLast={index === ids.length - 1}
                onMove={(offset) => move(index, offset)}
                onRemove={() => change(ids.filter((other) => other !== id))}
              />
            );
          })}
        </ol>
      )}
      <div className={styles['row']}>
        <div className={styles['fieldWidth']}>
          <Select
            label={t('store.addLabel')}
            value={addable.some((game) => game.id === candidate) ? candidate : NONE}
            disabled={full}
            options={[
              { value: NONE, label: t('store.choose') },
              ...addable.map((game) => ({ value: game.id, label: game.title })),
            ]}
            onChange={setCandidate}
          />
        </div>
        <Button
          variant="secondary"
          disabled={full || !addable.some((game) => game.id === candidate)}
          onClick={() => {
            change([...ids, candidate]);
            setCandidate(NONE);
          }}
        >
          {t('store.add')}
        </Button>
      </div>
      {full && <p className={styles['muted']}>{t('store.limit', { max: MAX_STORE_HEROES })}</p>}
      <div className={styles['row']}>
        <Button type="submit" disabled={!dirty || save.isPending}>
          {t('store.save')}
        </Button>
      </div>
      <ErrorText error={save.error} />
      {save.isSuccess && !dirty && (
        <p role="status" className={styles['status']}>
          {t('store.saved')}
        </p>
      )}
    </form>
  );
}

type HeroItemProps = {
  game: AdminGame;
  /** 1-based position in the list. */
  position: number;
  isLast: boolean;
  onMove: (offset: -1 | 1) => void;
  onRemove: () => void;
};

function HeroItem({ game, position, isLast, onMove, onRemove }: HeroItemProps) {
  const { t } = useTranslation('admin');
  const { title } = game;

  return (
    <li className={styles['heroItem']}>
      <span className={styles['heroPosition']} aria-hidden="true">
        {position}
      </span>
      {game.heroUrl !== null ? (
        <img
          className={styles['heroThumb']}
          src={game.heroUrl}
          alt={t('store.previewAlt', { title })}
          loading="lazy"
        />
      ) : (
        <div className={styles['heroThumb']} />
      )}
      <div className={styles['heroInfo']}>
        <span className={styles['heroTitle']}>{title}</span>
        {game.heroUrl === null && <p className={styles['muted']}>{t('store.noHeroImage')}</p>}
        {!game.published && <p className={styles['muted']}>{t('store.unpublished')}</p>}
      </div>
      <div className={styles['actions']}>
        <Button
          variant="secondary"
          size="sm"
          disabled={position === 1}
          aria-label={t('store.moveUp', { title })}
          onClick={() => onMove(-1)}
        >
          <span aria-hidden="true">↑</span>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={isLast}
          aria-label={t('store.moveDown', { title })}
          onClick={() => onMove(1)}
        >
          <span aria-hidden="true">↓</span>
        </Button>
        <Button
          variant="danger"
          size="sm"
          aria-label={t('store.remove', { title })}
          onClick={onRemove}
        >
          <span aria-hidden="true">×</span>
        </Button>
      </div>
    </li>
  );
}
